import { defaultLocale, Locale } from '@eos/shared-locale-utility/locale';
import { isSupportedLocale } from '@eos/shared-locale-utility/transformLocale';

/**
 * Matches a single BCP-47 language tag against the supported locales.
 * Returns the supported Locale on a hit, or undefined on a miss.
 * Follows the same rules as transformLocale, but does NOT fall back to the
 * default on a miss — this lets callers iterate over a preference list.
 */
const matchSingleLanguage = (language: string): Locale | undefined => {
  if (isSupportedLocale(language)) return language; // en-US, pt-PT, de ...

  // de-DE -> de, fr-CA -> fr
  if (language.length > 2) {
    const shortLanguage = language.substring(0, 2).toLowerCase();
    if (isSupportedLocale(shortLanguage)) return shortLanguage;
  }

  // pt -> pt-PT
  if (language.length === 2) {
    const longLanguage = `${language.toLowerCase()}-${language.toUpperCase()}`;
    if (isSupportedLocale(longLanguage)) return longLanguage;
  }

  return undefined;
};

/**
 * Returns the first supported Locale following the browser preference order;
 * falls back to the default locale when none are supported.
 * languages typically comes from navigator.languages, e.g. ['zh-CN','en-US','en'].
 */
export const getPreferredLocale = (
  languages: readonly string[] = []
): Locale => {
  for (const language of languages) {
    const matchedLocale = matchSingleLanguage(language);
    if (matchedLocale) return matchedLocale;
  }
  return defaultLocale;
};

/** Reads the browser language preference list (with graceful degradation). */
export const getBrowserLanguages = (): readonly string[] => {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages?.length
    ? navigator.languages
    : [navigator.language].filter(Boolean);
};
